import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, DatePicker, Spin, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDateTime, formatNumber } from 'pages/star-shared/utils';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import ImportFlow from '../components/import-flow';
import MonthStatusAlert from '../components/month-status-alert';
import { useDataImport } from '../context';
import { MonthArchive, MonthStatus } from '../types';
import { useCalendar } from '../use-calendar';
import {
  LOAD_FAILED_TEXT,
  MONTH_ARCHIVE_URL,
  MONTH_RE,
  MONTH_STATUS_META,
  dataImportLink,
  downloadFile,
  formatDayMonth,
  formatMonth,
  formatPeriod,
  monthTodo,
  shiftMonth,
  utcMonth,
} from '../utils';
import styles from '../style.module.scss';

/** Số tháng của bảng "Tình trạng các tháng". */
const OVERVIEW_MONTHS = 12;

/**
 * Tab "Data tháng": tháng đã qua nhập file đầy đủ cả tháng (ngày 01 → ngày cuối tháng).
 * File gốc được lưu lại để đối chiếu; khi có file tháng thì Bảng xếp hạng dùng số của file tháng.
 */
const MonthlyTab = () => {
  const { reportError, isAdmin } = useDataImport();
  // `?month=YYYY-MM`: nút "Mở Data tháng" ở tab khác / ở bảng tình trạng mở đúng tháng.
  const [params, setParams] = useSearchParams();
  const urlMonth = params.get('month');
  const linkedMonth =
    urlMonth && MONTH_RE.test(urlMonth) && urlMonth < utcMonth() ? urlMonth : null;
  const [month, setMonth] = useState(() => linkedMonth || shiftMonth(utcMonth(), -1));
  useEffect(() => {
    if (linkedMonth) setMonth(linkedMonth);
  }, [linkedMonth]);
  const pickMonth = (next: string) => {
    setMonth(next);
    // Giữ URL theo tháng đang xem (F5 / gửi link vẫn đúng tháng).
    const nextParams = new URLSearchParams(params);
    nextParams.set('month', next);
    setParams(nextParams, { replace: true });
  };
  const [overview, setOverview] = useState<MonthStatus[] | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: calendar, loading } = useCalendar(month, refreshKey);
  const [archives, setArchives] = useState<MonthArchive[]>([]);
  const [archivesLoading, setArchivesLoading] = useState(true);
  // Tải hỏng thì bảng phải nói "chưa tải được", không được nói "chưa lưu file nào".
  const [archivesFailed, setArchivesFailed] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);
  // Đang gửi lệnh áp dụng: khoá ô chọn tháng, đổi tháng lúc này làm mất khối kết quả.
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setArchivesLoading(true);
    setArchivesFailed(false);
    AxiosUtils.get(MONTH_ARCHIVE_URL, { month })
      .then((res: any) => {
        if (!cancelled) setArchives(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setArchives([]);
        setArchivesFailed(true);
        reportError(err, 'Không tải được danh sách file gốc');
      })
      .finally(() => {
        if (!cancelled) setArchivesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, refreshKey, reportError]);

  // Bảng "Tình trạng các tháng": tải lại cùng nút Tải lại / sau khi nhập.
  useEffect(() => {
    let cancelled = false;
    AxiosUtils.get(API.dataImportMonths, { count: OVERVIEW_MONTHS })
      .then((res: any) => {
        if (!cancelled) setOverview(Array.isArray(res?.data?.months) ? res.data.months : []);
      })
      .catch(() => {
        // Máy chủ cũ chưa có API này: ẩn bảng, không báo lỗi.
        if (!cancelled) setOverview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const download = async (archive: MonthArchive) => {
    setDownloading(archive.id);
    try {
      await downloadFile(`${MONTH_ARCHIVE_URL}/${archive.id}/download`, archive.fileName);
    } catch (err) {
      reportError(err, 'Không tải được file gốc');
    } finally {
      setDownloading(null);
    }
  };

  const currentMonth = calendar?.today?.slice(0, 7) || utcMonth();
  const monthText = formatMonth(month);
  const monthFile = calendar?.monthFile;
  const importedDates = (calendar?.days || [])
    .filter((day) => day.status === 'IMPORTED')
    .map((day) => day.date.slice(0, 10));
  const importedDays = importedDates.length;
  // Số cộng dồn không trọn tháng nhập từ trước (vd. 01/09 → 27/09): Bảng xếp hạng vẫn tính.
  const rawRanges = calendar?.legacyRanges;
  const legacyRanges = Array.isArray(rawRanges) ? rawRanges : [];
  // File ngày nằm trong khoảng cộng dồn không được cộng thêm vào Bảng xếp hạng (đặc tả 7.4),
  // nên chỉ đếm các ngày nằm ngoài khoảng đó.
  const extraDays = importedDates.filter(
    (date) =>
      !legacyRanges.some(
        (range) => date >= range.periodStart.slice(0, 10) && date <= range.periodEnd.slice(0, 10)
      )
  ).length;
  const legacyText = legacyRanges
    .map(
      (range) =>
        `${formatDayMonth(range.periodStart)} → ${formatDayMonth(range.periodEnd)} (${formatNumber(
          range.creators || 0
        )} Creator, ${formatNumber(range.diamonds || 0)} KC)`
    )
    .join('; ');
  const noMonthFileText = legacyRanges.length
    ? `Bảng xếp hạng tháng này đang tính từ số cộng dồn ${legacyText} nhập trước đây${
        extraDays > 0 ? ` và ${extraDays} ngày ngoài khoảng đó đã nhập theo file ngày` : ''
      }. Nhập file cả tháng để chốt số.`
    : importedDays > 0
    ? `Bảng xếp hạng tháng này đang tính từ ${importedDays} ngày đã nhập theo file ngày. Nhập file cả tháng để chốt số.`
    : 'Tháng này chưa có số liệu nào. Nhập file cả tháng để chốt số.';

  const archiveColumns: ColumnsType<MonthArchive> = [
    { title: 'File', dataIndex: 'fileName', render: (name: string) => name || '—' },
    {
      title: 'Số dòng',
      dataIndex: 'rows',
      align: 'right',
      width: 120,
      render: (rows: number) => formatNumber(rows || 0),
    },
    {
      title: 'Lưu lúc',
      dataIndex: 'createdAt',
      width: 180,
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: '',
      key: 'download',
      width: 140,
      render: (_, archive) => (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          loading={downloading === archive.id}
          onClick={() => download(archive)}
        >
          Tải về
        </Button>
      ),
    },
  ];

  // Bỏ các tháng cũ chưa có gì nằm trước tháng đầu tiên có dữ liệu (trước khi dùng hệ thống);
  // luôn giữ tháng này và tháng trước.
  let lastShown = (overview?.length || 0) - 1;
  while (lastShown > 1 && overview?.[lastShown]?.status === 'NO_DATA') lastShown -= 1;
  const shownMonths = overview ? overview.slice(0, lastShown + 1) : [];

  const overviewColumns: ColumnsType<MonthStatus> = [
    {
      title: 'Tháng',
      dataIndex: 'month',
      width: 100,
      render: (value: string) => formatMonth(value),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 190,
      render: (value: MonthStatus['status']) => {
        const meta = MONTH_STATUS_META[value] || MONTH_STATUS_META.FUTURE;
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Ngày có dữ liệu',
      key: 'days',
      width: 130,
      render: (_, row) =>
        row.status === 'MONTH_FILE' || row.status === 'NO_DATA'
          ? row.dayFiles
            ? `${row.dayFiles} ngày chi tiết`
            : '—'
          : `${row.dueDays - (row.missingDays?.length || 0)}/${row.dueDays}`,
    },
    {
      title: 'Việc cần làm',
      key: 'todo',
      render: (_, row) => <span className={styles.monthTodo}>{monthTodo(row)}</span>,
    },
    {
      title: '',
      key: 'open',
      width: 130,
      render: (_, row) =>
        row.month >= currentMonth || row.status === 'DAILY_INCOMPLETE' ? (
          <Link to={dataImportLink('ngay', row.month)}>
            <Button size="small">Mở Data ngày</Button>
          </Link>
        ) : (
          <Button size="small" disabled={row.month === month} onClick={() => pickMonth(row.month)}>
            {row.month === month ? 'Đang xem' : 'Xem tháng này'}
          </Button>
        ),
    },
  ];

  return (
    <div>
      <div className={styles.toolbar}>
        <span>Tháng đã qua:</span>
        <DatePicker
          picker="month"
          allowClear={false}
          format="MM/YYYY"
          value={dayjs(`${month}-01`)}
          disabled={applying}
          disabledDate={(date) => date.format('YYYY-MM') >= currentMonth}
          onChange={(date) => date && pickMonth(date.format('YYYY-MM'))}
        />
        <Button
          icon={<ReloadOutlined />}
          loading={loading || archivesLoading}
          onClick={() => setRefreshKey((key) => key + 1)}
        >
          Tải lại
        </Button>
      </div>

      <div className={styles.section}>
        {loading && !calendar ? (
          <Spin />
        ) : monthFile ? (
          <Alert
            type="success"
            showIcon
            message={`Tháng ${monthText} đã có file tháng`}
            description={
              <>
                Giai đoạn {formatPeriod(monthFile.periodStart, monthFile.periodEnd)} ·{' '}
                {formatNumber(monthFile.creators || 0)} Creator ·{' '}
                {formatNumber(monthFile.diamonds || 0)} KC
                {monthFile.importedAt ? ` · nhập lúc ${formatDateTime(monthFile.importedAt)}` : ''}
                {monthFile.importedByName ? ` bởi ${monthFile.importedByName}` : ''}. Nhập lại sẽ
                ghi đè số cũ.
              </>
            }
          />
        ) : calendar?.status ? (
          <MonthStatusAlert
            status={calendar.status}
            view="thang"
            isAdmin={isAdmin}
            legacyDetail={noMonthFileText}
          />
        ) : calendar ? (
          <Alert
            type="warning"
            showIcon
            message={`Tháng ${monthText} chưa có file tháng`}
            description={noMonthFileText}
          />
        ) : (
          <Alert
            type="error"
            showIcon
            message={`Chưa tải được trạng thái tháng ${monthText}`}
            description={LOAD_FAILED_TEXT}
          />
        )}
      </div>

      <Card className={styles.section}>
        <div className={styles.head}>
          <h2 className={styles.headTitle}>Nhập file cả tháng {monthText}</h2>
        </div>
        <ImportFlow
          key={month}
          kind="MONTHLY"
          month={month}
          onApplied={() => setRefreshKey((key) => key + 1)}
          onApplyingChange={setApplying}
          hint={
            <>
              File "Dữ liệu nhà sáng tạo" tải từ TikTok LIVE Backstage, chọn giai đoạn từ ngày 01
              đến ngày cuối của tháng {monthText}. Dùng cho tháng đã qua không có dữ liệu từng ngày;
              tháng đã nhập theo ngày thì bù đủ ngày ở tab Data ngày. File gốc được lưu lại để đối
              chiếu.
            </>
          }
        />
      </Card>

      <Card className={styles.section}>
        <div className={styles.head}>
          <h2 className={styles.headTitle}>File gốc đã lưu của tháng {monthText}</h2>
        </div>
        <Table
          size="small"
          rowKey="id"
          loading={archivesLoading}
          dataSource={archives}
          columns={archiveColumns}
          pagination={false}
          scroll={{ x: 640 }}
          locale={{
            emptyText: archivesFailed ? LOAD_FAILED_TEXT : 'Tháng này chưa lưu file gốc nào.',
          }}
        />
      </Card>

      {shownMonths.length > 0 && (
        <Card className={styles.section}>
          <div className={styles.head}>
            <h2 className={styles.headTitle}>Tình trạng các tháng gần nhất</h2>
          </div>
          <p className={styles.note}>
            Tháng đang chạy nhập từng ngày. Tháng đã nhập theo ngày phải bù đủ ngày (đủ ngày thì
            không cần file tháng). Tháng đã qua không có dữ liệu ngày thì nhập 1 file cả tháng.
          </p>
          <Table
            size="small"
            rowKey="month"
            dataSource={shownMonths}
            columns={overviewColumns}
            pagination={false}
            scroll={{ x: 640 }}
            rowClassName={(row) => (row.month === month ? 'ant-table-row-selected' : '')}
          />
        </Card>
      )}
    </div>
  );
};

export default MonthlyTab;
